import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { THEME } from '../../theme';

const STORE_COLORS = ['#2cbb5d', '#3b82f6', '#f59e0b', '#22d3ee', '#ef4444'];
const ROUTE_COLORS = ['#2cbb5d', '#3b82f6', '#f59e0b', '#22d3ee', '#a1a1aa'];

const chartFrame = {
    width: '100%',
    overflow: 'hidden',
    borderRadius: 2,
    border: `1px solid ${THEME.border}`,
    bgcolor: '#0a0a0a',
    position: 'relative',
};

function useRafProgress(durationMs = 4000, deps = []) {
    const [t, setT] = useState(0);
    const start = useRef(performance.now());
    useEffect(() => {
        start.current = performance.now();
        let frame;
        const tick = (now) => {
            const elapsed = (now - start.current) % durationMs;
            setT(elapsed / durationMs);
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return t;
}

/** EOQ: animated inventory sawtooth + TC / ordering / holding cost curves with Q* marker */
export function EoqVisualization({ result, params }) {
    const D = Number(params?.D ?? 1200);
    const K = Number(params?.K ?? 50);
    const h = Number(params?.h ?? 2.5);
    const Q = Number(result?.Q_star ?? Math.sqrt((2 * D * K) / h));
    const progress = useRafProgress(5200, [Q, D, K, h]);

    const w = 560;
    const hChart = 168;
    const pad = { l: 44, r: 16, t: 28, b: 28 };

    const costCurve = useMemo(() => {
        const qMax = Math.max(Q * 2.4, 40);
        const pts = [];
        for (let i = 0; i <= 80; i++) {
            const q = (qMax / 80) * Math.max(i, 0.5);
            const ordering = (D / q) * K;
            const holding = (q / 2) * h;
            pts.push({ q, ordering, holding, total: ordering + holding });
        }
        return { pts, qMax };
    }, [D, K, h, Q]);

    const tcMax = Math.max(...costCurve.pts.map((p) => p.total), 1);
    const xCost = (q) => pad.l + (q / costCurve.qMax) * (w - pad.l - pad.r);
    const yCost = (v) => pad.t + ((tcMax - v) / tcMax) * (hChart - pad.t - pad.b);

    const pathOf = (key) =>
        costCurve.pts
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xCost(p.q)} ${yCost(p[key])}`)
            .join(' ');

    // Sawtooth: inventory cycles Q -> 0
    const cycle = (progress * 3) % 1;
    const inv = Q * (1 - cycle);
    const sawW = 560;
    const sawH = 140;
    const sPad = { l: 44, r: 16, t: 28, b: 24 };
    const cyclesDrawn = 3;
    const xSaw = (u) => sPad.l + u * (sawW - sPad.l - sPad.r);
    const ySaw = (v) => sPad.t + ((Q - v) / Math.max(Q, 1e-9)) * (sawH - sPad.t - sPad.b);
    const sawPath = Array.from({ length: cyclesDrawn }, (_, c) => {
        const x0 = c / cyclesDrawn;
        const x1 = (c + 1) / cyclesDrawn;
        return `M ${xSaw(x0)} ${ySaw(Q)} L ${xSaw(x1)} ${ySaw(0)}`;
    }).join(' ');

    const markerU = (Math.floor(progress * 3) + cycle) / cyclesDrawn;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={chartFrame}>
                <svg width="100%" viewBox={`0 0 ${sawW} ${sawH}`} style={{ display: 'block' }}>
                    <defs>
                        <linearGradient id="eoqFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={THEME.primary} stopOpacity="0.35" />
                            <stop offset="100%" stopColor={THEME.primary} stopOpacity="0.02" />
                        </linearGradient>
                    </defs>
                    <text x={sPad.l} y={18} fill={THEME.secondary} fontSize="11">
                        Inventory sawtooth · Q* = {Q.toFixed(1)}
                    </text>
                    <path d={sawPath} fill="none" stroke="#2a2a2a" strokeWidth="1.5" />
                    <path
                        d={`${sawPath} L ${xSaw(1)} ${ySaw(0)} L ${xSaw(0)} ${ySaw(0)} Z`}
                        fill="url(#eoqFill)"
                        opacity="0.45"
                    />
                    <path d={sawPath} fill="none" stroke={THEME.primary} strokeWidth="2.2" />
                    <line
                        x1={sPad.l}
                        y1={ySaw(0)}
                        x2={sawW - sPad.r}
                        y2={ySaw(0)}
                        stroke="#333"
                        strokeDasharray="4 4"
                    />
                    <circle cx={xSaw(markerU)} cy={ySaw(inv)} r="6" fill={THEME.primary}>
                        <animate
                            attributeName="r"
                            values="5;7;5"
                            dur="1.2s"
                            repeatCount="indefinite"
                        />
                    </circle>
                    <text x={xSaw(markerU) + 10} y={ySaw(inv) - 8} fill={THEME.textMain} fontSize="11">
                        I = {inv.toFixed(1)}
                    </text>
                </svg>
            </Box>

            <Box sx={chartFrame}>
                <svg width="100%" viewBox={`0 0 ${w} ${hChart}`} style={{ display: 'block' }}>
                    <text x={pad.l} y={18} fill={THEME.secondary} fontSize="11">
                        Cost curves vs order quantity
                    </text>
                    <path d={pathOf('ordering')} fill="none" stroke="#3b82f6" strokeWidth="1.8" />
                    <path d={pathOf('holding')} fill="none" stroke="#f59e0b" strokeWidth="1.8" />
                    <path d={pathOf('total')} fill="none" stroke={THEME.primary} strokeWidth="2.4" />
                    <line
                        x1={xCost(Q)}
                        y1={pad.t}
                        x2={xCost(Q)}
                        y2={hChart - pad.b}
                        stroke={THEME.primary}
                        strokeDasharray="5 4"
                        strokeWidth="1.5"
                        opacity="0.85"
                    />
                    <circle
                        cx={xCost(Q)}
                        cy={yCost((D / Q) * K + (Q / 2) * h)}
                        r="7"
                        fill={THEME.primary}
                        stroke="#0a0a0a"
                        strokeWidth="2"
                    >
                        <animate
                            attributeName="opacity"
                            values="1;0.55;1"
                            dur="1.6s"
                            repeatCount="indefinite"
                        />
                    </circle>
                    <text
                        x={xCost(Q) + 8}
                        y={yCost((D / Q) * K + (Q / 2) * h) - 10}
                        fill={THEME.textMain}
                        fontSize="11"
                        fontWeight="700"
                    >
                        Q*
                    </text>
                    <g fontSize="10" fill={THEME.secondary}>
                        <circle cx={pad.l} cy={hChart - 10} r="3" fill={THEME.primary} />
                        <text x={pad.l + 8} y={hChart - 7}>
                            TC
                        </text>
                        <circle cx={pad.l + 40} cy={hChart - 10} r="3" fill="#3b82f6" />
                        <text x={pad.l + 48} y={hChart - 7}>
                            Ordering
                        </text>
                        <circle cx={pad.l + 110} cy={hChart - 10} r="3" fill="#f59e0b" />
                        <text x={pad.l + 118} y={hChart - 7}>
                            Holding
                        </text>
                    </g>
                </svg>
            </Box>
        </Box>
    );
}

/** SDE: animated inventory trajectories with dispatch pulse markers */
export function SdeVisualization({ result }) {
    const times = result?.times || [];
    const mean = useMemo(() => result?.inventory_mean || [], [result?.inventory_mean]);
    const sample = useMemo(() => result?.inventory_sample || [], [result?.inventory_sample]);
    const dispatchTimes = result?.dispatch_times || [];
    const progress = useRafProgress(7000, [times.length, mean.length]);

    const series = useMemo(() => {
        if (!mean.length) return [];
        const nStores = mean[0].length;
        return Array.from({ length: nStores }, (_, s) => mean.map((row) => row[s]));
    }, [mean]);

    const sampleSeries = useMemo(() => {
        if (!sample.length) return [];
        const nStores = sample[0].length;
        return Array.from({ length: nStores }, (_, s) => sample.map((row) => row[s]));
    }, [sample]);

    if (!times.length || !series.length) return null;

    const w = 560;
    const h = 200;
    const pad = { l: 40, r: 14, t: 28, b: 24 };
    const flat = [...series.flat(), ...sampleSeries.flat(), 0];
    const ymin = Math.min(...flat);
    const ymax = Math.max(...flat, 1);
    const visible = Math.max(2, Math.floor(1 + progress * (times.length - 1)));

    const x = (i) => pad.l + (i / Math.max(times.length - 1, 1)) * (w - pad.l - pad.r);
    const y = (v) => pad.t + ((ymax - v) / Math.max(ymax - ymin, 1e-9)) * (h - pad.t - pad.b);

    const pathFor = (arr, n) =>
        arr
            .slice(0, n)
            .map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`)
            .join(' ');

    const playT = times[visible - 1] ?? 0;

    return (
        <Box sx={chartFrame}>
            <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
                <text x={pad.l} y={18} fill={THEME.secondary} fontSize="11">
                    Inventory trajectories · t = {playT.toFixed(1)}
                </text>
                <line
                    x1={pad.l}
                    y1={y(0)}
                    x2={w - pad.r}
                    y2={y(0)}
                    stroke="#333"
                    strokeDasharray="4 4"
                />
                {sampleSeries.map((arr, sIdx) => (
                    <path
                        key={`s${sIdx}`}
                        d={pathFor(arr, visible)}
                        fill="none"
                        stroke={STORE_COLORS[sIdx % STORE_COLORS.length]}
                        strokeWidth="1"
                        opacity="0.35"
                        strokeDasharray="3 3"
                    />
                ))}
                {series.map((arr, sIdx) => (
                    <g key={`m${sIdx}`}>
                        <path
                            d={pathFor(arr, visible)}
                            fill="none"
                            stroke={STORE_COLORS[sIdx % STORE_COLORS.length]}
                            strokeWidth="2.2"
                        />
                        <circle
                            cx={x(visible - 1)}
                            cy={y(arr[visible - 1])}
                            r="4.5"
                            fill={STORE_COLORS[sIdx % STORE_COLORS.length]}
                        />
                    </g>
                ))}
                {dispatchTimes.map((dt, i) => {
                    const idx = times.findIndex((t) => Math.abs(t - dt) < 1e-6) ;
                    const ti = idx >= 0 ? idx : Math.round(dt / (times[1] - times[0] || 0.1));
                    if (ti > visible) return null;
                    return (
                        <g key={`d${i}`}>
                            <line
                                x1={x(ti)}
                                y1={pad.t}
                                x2={x(ti)}
                                y2={h - pad.b}
                                stroke={THEME.accent}
                                strokeWidth="1"
                                opacity="0.55"
                            />
                            <circle cx={x(ti)} cy={pad.t + 4} r="4" fill={THEME.accent}>
                                <animate
                                    attributeName="r"
                                    values="3;6;3"
                                    dur="1.4s"
                                    repeatCount="indefinite"
                                />
                            </circle>
                        </g>
                    );
                })}
            </svg>
            <Typography variant="caption" sx={{ color: THEME.secondary, px: 1.5, pb: 1, display: 'block' }}>
                Solid = mean across paths · dashed = sample path · red ticks = dispatches
            </Typography>
        </Box>
    );
}

/** Transshipment / facility: animated shipment flow ribbons */
export function FlowVisualization({ result, problem }) {
    const rows =
        problem === 'facility_location'
            ? (result?.allocations || []).map((a) => ({
                  from: a.facility,
                  to: a.customer,
                  qty: a.qty ?? a.fraction,
                  label: `${((a.fraction || 0) * 100).toFixed(0)}%`,
              }))
            : (result?.shipments || []).map((s) => ({
                  from: s.from,
                  to: s.to,
                  qty: s.qty,
                  label: Number(s.qty).toFixed(1),
              }));

    const progress = useRafProgress(3500, [rows.length, result?.total_cost]);
    if (!rows.length) return null;

    const maxQty = Math.max(...rows.map((r) => Number(r.qty) || 0), 1);
    const w = 560;
    const rowH = 36;
    const h = Math.min(320, 40 + rows.slice(0, 10).length * rowH);

    return (
        <Box sx={chartFrame}>
            <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
                <text x={16} y={20} fill={THEME.secondary} fontSize="11">
                    {problem === 'facility_location' ? 'Allocation flows' : 'Optimal shipments'} · detail (primary animation on network edges)
                </text>
                {rows.slice(0, 10).map((r, i) => {
                    const y0 = 36 + i * rowH;
                    const barW = 180 + (Number(r.qty) / maxQty) * 220;
                    const pulse = 0.55 + 0.45 * Math.sin((progress * Math.PI * 2) + i * 0.7);
                    const packetX = 140 + ((progress + i * 0.12) % 1) * Math.max(barW - 24, 40);
                    return (
                        <g key={i}>
                            <text x={16} y={y0 + 4} fill={THEME.textMain} fontSize="11">
                                {r.from}
                            </text>
                            <text x={w - 16} y={y0 + 4} fill={THEME.textMain} fontSize="11" textAnchor="end">
                                {r.to}
                            </text>
                            <rect
                                x={140}
                                y={y0 - 8}
                                width={barW}
                                height={10}
                                rx={5}
                                fill={THEME.primary}
                                opacity={0.15 + 0.25 * pulse}
                            />
                            <rect
                                x={140}
                                y={y0 - 8}
                                width={barW}
                                height={10}
                                rx={5}
                                fill="none"
                                stroke={THEME.primary}
                                strokeWidth="1.2"
                                opacity={0.5 + 0.5 * pulse}
                            />
                            <circle cx={packetX} cy={y0 - 3} r="4.5" fill={THEME.primary}>
                                <animate
                                    attributeName="opacity"
                                    values="0.6;1;0.6"
                                    dur="1s"
                                    repeatCount="indefinite"
                                />
                            </circle>
                            <text x={140 + barW / 2} y={y0 + 18} fill={THEME.secondary} fontSize="10" textAnchor="middle">
                                {r.label}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
}

/** CVRP: animated vehicles traversing route chains */
export function CvrpVisualization({ result }) {
    const routes = result?.routes || [];
    const progress = useRafProgress(5000, [routes.length, result?.total_cost]);
    if (!routes.length) return null;

    const w = 560;
    const rowH = 52;
    const h = 36 + routes.length * rowH;

    return (
        <Box sx={chartFrame}>
            <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
                <text x={16} y={20} fill={THEME.secondary} fontSize="11">
                    Vehicle routes · detail (primary animation on network edges)
                </text>
                {routes.map((r, ri) => {
                    const path = r.path || [];
                    const y0 = 44 + ri * rowH;
                    const n = Math.max(path.length, 2);
                    const color = ROUTE_COLORS[ri % ROUTE_COLORS.length];
                    const xs = path.map((_, i) => 28 + (i / (n - 1)) * (w - 56));
                    const vehIdx = progress * (n - 1);
                    const i0 = Math.floor(vehIdx);
                    const frac = vehIdx - i0;
                    const cx =
                        i0 >= n - 1
                            ? xs[n - 1]
                            : xs[i0] + (xs[i0 + 1] - xs[i0]) * frac;
                    return (
                        <g key={r.vehicle ?? ri}>
                            <text x={16} y={y0 - 14} fill={color} fontSize="11" fontWeight="700">
                                V{r.vehicle} · load {Number(r.load).toFixed(0)} · dist{' '}
                                {Number(r.distance).toFixed(2)}
                            </text>
                            {xs.slice(0, -1).map((x, i) => (
                                <line
                                    key={i}
                                    x1={x}
                                    y1={y0}
                                    x2={xs[i + 1]}
                                    y2={y0}
                                    stroke={color}
                                    strokeWidth="2"
                                    opacity="0.45"
                                />
                            ))}
                            {xs.map((x, i) => (
                                <g key={`n${i}`}>
                                    <circle cx={x} cy={y0} r="7" fill="#121214" stroke={color} strokeWidth="2" />
                                    <text
                                        x={x}
                                        y={y0 + 22}
                                        fill={THEME.secondary}
                                        fontSize="9"
                                        textAnchor="middle"
                                    >
                                        {String(path[i]).slice(0, 8)}
                                    </text>
                                </g>
                            ))}
                            <circle cx={cx} cy={y0} r="8" fill={color} stroke="#0a0a0a" strokeWidth="2">
                                <animate
                                    attributeName="r"
                                    values="7;9;7"
                                    dur="0.9s"
                                    repeatCount="indefinite"
                                />
                            </circle>
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
}

export default function SolverResultVisual({ problem, result, params }) {
    if (!result) return null;
    if (problem === 'eoq') return <EoqVisualization result={result} params={params} />;
    if (problem === 'sde_routing') return <SdeVisualization result={result} />;
    if (problem === 'transshipment' || problem === 'facility_location') {
        return <FlowVisualization result={result} problem={problem} />;
    }
    if (problem === 'cvrp') return <CvrpVisualization result={result} />;
    return null;
}
